"use client";

import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { Id } from "@convex/_generated/dataModel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ToastAction } from "@/components/ui/toast";
import { toast } from "@/hooks/use-toast";
import { getErrorMessage } from "@/lib/errors";

interface ExpenseDeleteDialogProps {
  expenseId: Id<"expenses">;
  expenseType: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ExpenseDeleteDialog({
  expenseId,
  expenseType,
  open,
  onOpenChange,
}: ExpenseDeleteDialogProps) {
  const expense = useQuery(api.expenses.getExpense, open ? { id: expenseId } : "skip");
  const deleteExpense = useMutation(api.expenses.deleteExpense);
  const createExpense = useMutation(api.expenses.createExpense);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!expense) return;

    // Store expense data before deletion for undo
    const expenseData = {
      blockId: expense.blockId,
      type: expense.type,
      amount: expense.amount,
      date: expense.date,
      gallons: expense.gallons,
      pricePerGallon: expense.pricePerGallon,
      rewardsPercent: expense.rewardsPercent,
      upsidePerGallon: expense.upsidePerGallon,
      description: expense.description,
    };

    setIsDeleting(true);
    try {
      await deleteExpense({ id: expenseId });
      onOpenChange(false);

      toast({
        title: "Expense deleted",
        description: `${expenseType} expense removed`,
        duration: 5000,
        action: (
          <ToastAction
            altText="Undo delete"
            onClick={async () => {
              try {
                await createExpense(expenseData);
                toast({
                  title: "Expense restored",
                  description: `${expenseType} expense has been restored`,
                });
              } catch {
                toast({
                  title: "Failed to restore",
                  description: "Could not restore the expense",
                  variant: "destructive",
                });
              }
            }}
          >
            Undo
          </ToastAction>
        ),
      });
    } catch (error) {
      toast({
        title: "Delete failed",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Expense</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this {expenseType} expense?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting || !expense}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
